/**
 * AUTO_INSURANCE_QuoteFunnel_v01
 * Tech: React + TypeScript + React-Bootstrap + react-bootstrap-icons
 *
 * Features:
 * - 4-step quote wizard (Driver → Vehicle → Coverage → Review & Submit)
 * - Progress bar + step indicators
 * - Basic required-field validation
 * - Mock submit with success message
 */

import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  ProgressBar,
  Alert,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  PersonFill,
  CarFrontFill,
  ShieldLockFill,
  CheckCircleFill,
  TelephoneFill,
  EnvelopeFill,
} from "react-bootstrap-icons";

type Step = 1 | 2 | 3 | 4;

interface DriverInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  licenseState: string;
  licenseNumber: string;
  maritalStatus: string;
  sr22Needed: string; // "yes" | "no" | ""
}

interface VehicleInfo {
  year: string;
  make: string;
  model: string;
  vin: string;
  primaryUse: string;
  annualMileage: string;
  ownership: string; // own/lease/finance
  garagingZip: string;
}

interface CoveragePreferences {
  coverageLevel: string; // "state-minimum" | "standard" | "premium" | ""
  collisionDeductible: string;
  comprehensiveDeductible: string;
  roadsideAssistance: boolean;
  rentalReimbursement: boolean;
  uninsuredMotorist: string;
}

interface MetaInfo {
  preferredContactMethod: string; // "phone" | "email" | ""
  bestTimeToContact: string;
  consentToContact: boolean;
}

type ErrorMap = Record<string, string>;

const AutoInsuranceQuoteFunnel_2025_11_27_chat: React.FC = () => {
  const [step, setStep] = useState<Step>(1);
  const [driver, setDriver] = useState<DriverInfo>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    licenseState: "",
    licenseNumber: "",
    maritalStatus: "",
    sr22Needed: "",
  });

  const [vehicle, setVehicle] = useState<VehicleInfo>({
    year: "",
    make: "",
    model: "",
    vin: "",
    primaryUse: "",
    annualMileage: "",
    ownership: "",
    garagingZip: "",
  });

  const [coverage, setCoverage] = useState<CoveragePreferences>({
    coverageLevel: "",
    collisionDeductible: "",
    comprehensiveDeductible: "",
    roadsideAssistance: true,
    rentalReimbursement: false,
    uninsuredMotorist: "",
  });

  const [meta, setMeta] = useState<MetaInfo>({
    preferredContactMethod: "",
    bestTimeToContact: "",
    consentToContact: true,
  });

  const [errors, setErrors] = useState<ErrorMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const totalSteps: Step = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    const validationErrors = validateStep(step);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    if (step < totalSteps) {
      setStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setErrors({});
      setStep((prev) => (prev - 1) as Step);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateStep(step, true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});
    setSubmitted(false);

    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      // In real world, you’d send driver/vehicle/coverage/meta to your API or email service
      console.log("QUOTE_REQUEST_PAYLOAD", {
        driver,
        vehicle,
        coverage,
        meta,
      });
    }, 1500);
  };

  const validateStep = (s: Step, finalCheck = false): ErrorMap => {
    const newErrors: ErrorMap = {};

    if (s === 1 || finalCheck) {
      if (!driver.firstName.trim()) newErrors.firstName = "First name is required.";
      if (!driver.lastName.trim()) newErrors.lastName = "Last name is required.";
      if (!driver.email.trim()) newErrors.email = "Email is required.";
      if (!driver.phone.trim()) newErrors.phone = "Phone is required.";
      if (!driver.dateOfBirth.trim()) newErrors.dateOfBirth = "Date of birth is required.";
      if (!driver.licenseState.trim()) newErrors.licenseState = "License state is required.";
      if (!driver.licenseNumber.trim()) newErrors.licenseNumber = "License number is required.";
      if (!driver.maritalStatus.trim()) newErrors.maritalStatus = "Marital status is required.";
      if (!driver.sr22Needed.trim()) newErrors.sr22Needed = "Please indicate SR-22 requirement.";
    }

    if (s === 2 || finalCheck) {
      if (!vehicle.year.trim()) newErrors.year = "Year is required.";
      if (!vehicle.make.trim()) newErrors.make = "Make is required.";
      if (!vehicle.model.trim()) newErrors.model = "Model is required.";
      if (!vehicle.primaryUse.trim()) newErrors.primaryUse = "Primary use is required.";
      if (!vehicle.ownership.trim()) newErrors.ownership = "Ownership status is required.";
      if (!vehicle.garagingZip.trim()) newErrors.garagingZip = "Garaging ZIP is required.";
    }

    if (s === 3 || finalCheck) {
      if (!coverage.coverageLevel.trim())
        newErrors.coverageLevel = "Please choose a coverage level.";
      if (!coverage.collisionDeductible.trim())
        newErrors.collisionDeductible = "Collision deductible is required.";
      if (!coverage.comprehensiveDeductible.trim())
        newErrors.comprehensiveDeductible = "Comprehensive deductible is required.";
      if (!coverage.uninsuredMotorist.trim())
        newErrors.uninsuredMotorist = "Please select uninsured motorist limits.";
    }

    if (s === 4 || finalCheck) {
      if (!meta.preferredContactMethod.trim())
        newErrors.preferredContactMethod = "Preferred contact method is required.";
      if (!meta.bestTimeToContact.trim())
        newErrors.bestTimeToContact = "Best time to contact is required.";
      if (!meta.consentToContact)
        newErrors.consentToContact = "We need your permission to contact you.";
    }

    return newErrors;
  };

  const hasError = (field: string) => !!errors[field];

  // --- RENDER HELPERS -------------------------------------------------------

  const renderStepHeader = () => {
    const stepTitleMap: Record<Step, string> = {
      1: "Driver Information",
      2: "Vehicle Information",
      3: "Coverage Preferences",
      4: "Review & Submit",
    };
    const stepIconMap: Record<Step, JSX.Element> = {
      1: <PersonFill className="me-2" />,
      2: <CarFrontFill className="me-2" />,
      3: <ShieldLockFill className="me-2" />,
      4: <CheckCircleFill className="me-2" />,
    };
    return (
      <div className="d-flex align-items-center mb-3">
        <div className="me-2 fs-4">{stepIconMap[step]}</div>
        <div>
          <div className="text-muted small">Step {step} of {totalSteps}</div>
          <h4 className="mb-0">{stepTitleMap[step]}</h4>
        </div>
      </div>
    );
  };

  const renderDriverStep = () => (
    <>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="firstName">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              value={driver.firstName}
              isInvalid={hasError("firstName")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, firstName: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="lastName">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              value={driver.lastName}
              isInvalid={hasError("lastName")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, lastName: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="email">
            <Form.Label>Email</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <EnvelopeFill />
              </InputGroup.Text>
              <Form.Control
                type="email"
                value={driver.email}
                isInvalid={hasError("email")}
                onChange={(e) =>
                  setDriver((prev) => ({ ...prev, email: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="phone">
            <Form.Label>Phone</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <TelephoneFill />
              </InputGroup.Text>
              <Form.Control
                type="tel"
                value={driver.phone}
                isInvalid={hasError("phone")}
                onChange={(e) =>
                  setDriver((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="dateOfBirth">
            <Form.Label>Date of Birth</Form.Label>
            <Form.Control
              type="date"
              value={driver.dateOfBirth}
              isInvalid={hasError("dateOfBirth")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, dateOfBirth: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.dateOfBirth}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="licenseState">
            <Form.Label>License State</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. CO"
              value={driver.licenseState}
              isInvalid={hasError("licenseState")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, licenseState: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.licenseState}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="licenseNumber">
            <Form.Label>License Number</Form.Label>
            <Form.Control
              type="text"
              value={driver.licenseNumber}
              isInvalid={hasError("licenseNumber")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, licenseNumber: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.licenseNumber}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="maritalStatus">
            <Form.Label>Marital Status</Form.Label>
            <Form.Select
              value={driver.maritalStatus}
              isInvalid={hasError("maritalStatus")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, maritalStatus: e.target.value }))
              }
            >
              <option value="">Select...</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="divorced">Divorced</option>
              <option value="widowed">Widowed</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.maritalStatus}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="sr22Needed">
            <Form.Label>Do you need an SR-22 filing?</Form.Label>
            <Form.Select
              value={driver.sr22Needed}
              isInvalid={hasError("sr22Needed")}
              onChange={(e) =>
                setDriver((prev) => ({ ...prev, sr22Needed: e.target.value }))
              }
            >
              <option value="">Select...</option>
              <option value="no">No</option>
              <option value="yes">Yes / Not sure</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.sr22Needed}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const renderVehicleStep = () => (
    <>
      <Row className="mb-3">
        <Col md={3}>
          <Form.Group controlId="year">
            <Form.Label>Year</Form.Label>
            <Form.Control
              type="text"
              value={vehicle.year}
              isInvalid={hasError("year")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, year: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.year}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={5}>
          <Form.Group controlId="make">
            <Form.Label>Make</Form.Label>
            <Form.Control
              type="text"
              value={vehicle.make}
              isInvalid={hasError("make")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, make: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.make}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="model">
            <Form.Label>Model</Form.Label>
            <Form.Control
              type="text"
              value={vehicle.model}
              isInvalid={hasError("model")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, model: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.model}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="vin">
            <Form.Label>VIN (optional but helpful)</Form.Label>
            <Form.Control
              type="text"
              value={vehicle.vin}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, vin: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="garagingZip">
            <Form.Label>Garaging ZIP</Form.Label>
            <Form.Control
              type="text"
              value={vehicle.garagingZip}
              isInvalid={hasError("garagingZip")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, garagingZip: e.target.value }))
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.garagingZip}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="primaryUse">
            <Form.Label>Primary Use</Form.Label>
            <Form.Select
              value={vehicle.primaryUse}
              isInvalid={hasError("primaryUse")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, primaryUse: e.target.value }))
              }
            >
              <option value="">Select...</option>
              <option value="commute">Commute to work/school</option>
              <option value="pleasure">Pleasure / personal</option>
              <option value="business">Business / commercial</option>
              <option value="rideshare">Rideshare (Uber/Lyft)</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.primaryUse}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="annualMileage">
            <Form.Label>Estimated Annual Mileage</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={vehicle.annualMileage}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, annualMileage: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="ownership">
            <Form.Label>Ownership</Form.Label>
            <Form.Select
              value={vehicle.ownership}
              isInvalid={hasError("ownership")}
              onChange={(e) =>
                setVehicle((prev) => ({ ...prev, ownership: e.target.value }))
              }
            >
              <option value="">Select...</option>
              <option value="own">Own (paid off)</option>
              <option value="finance">Financed</option>
              <option value="lease">Leased</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.ownership}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );

  const renderCoverageStep = () => (
    <>
      <Row className="mb-3">
        <Col>
          <Form.Group controlId="coverageLevel">
            <Form.Label>Preferred Coverage Level</Form.Label>
            <div className="d-flex flex-column flex-md-row gap-2">
              {[
                {
                  id: "state-minimum",
                  label: "State Minimum",
                  desc: "Meets legal minimum requirements.",
                },
                {
                  id: "standard",
                  label: "Standard",
                  desc: "Balanced protection & price.",
                },
                {
                  id: "premium",
                  label: "Premium",
                  desc: "Highest protection, lowest out-of-pocket risk.",
                },
              ].map((opt) => (
                <Card
                  key={opt.id}
                  className={`flex-fill ${
                    coverage.coverageLevel === opt.id ? "border-primary" : ""
                  }`}
                  role="button"
                  onClick={() =>
                    setCoverage((prev) => ({ ...prev, coverageLevel: opt.id }))
                  }
                >
                  <Card.Body>
                    <Form.Check
                      type="radio"
                      id={`cov-${opt.id}`}
                      name="coverageLevel"
                      label={opt.label}
                      checked={coverage.coverageLevel === opt.id}
                      onChange={() =>
                        setCoverage((prev) => ({ ...prev, coverageLevel: opt.id }))
                      }
                    />
                    <div className="small text-muted mt-1">{opt.desc}</div>
                  </Card.Body>
                </Card>
              ))}
            </div>
            {hasError("coverageLevel") && (
              <div className="text-danger small mt-1">
                {errors.coverageLevel}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="collisionDeductible">
            <Form.Label>Collision Deductible</Form.Label>
            <Form.Select
              value={coverage.collisionDeductible}
              isInvalid={hasError("collisionDeductible")}
              onChange={(e) =>
                setCoverage((prev) => ({
                  ...prev,
                  collisionDeductible: e.target.value,
                }))
              }
            >
              <option value="">Select...</option>
              <option value="250">$250</option>
              <option value="500">$500</option>
              <option value="1000">$1,000</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.collisionDeductible}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="comprehensiveDeductible">
            <Form.Label>Comprehensive Deductible</Form.Label>
            <Form.Select
              value={coverage.comprehensiveDeductible}
              isInvalid={hasError("comprehensiveDeductible")}
              onChange={(e) =>
                setCoverage((prev) => ({
                  ...prev,
                  comprehensiveDeductible: e.target.value,
                }))
              }
            >
              <option value="">Select...</option>
              <option value="250">$250</option>
              <option value="500">$500</option>
              <option value="1000">$1,000</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.comprehensiveDeductible}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="uninsuredMotorist">
            <Form.Label>Uninsured / Underinsured Motorist Limits</Form.Label>
            <Form.Select
              value={coverage.uninsuredMotorist}
              isInvalid={hasError("uninsuredMotorist")}
              onChange={(e) =>
                setCoverage((prev) => ({
                  ...prev,
                  uninsuredMotorist: e.target.value,
                }))
              }
            >
              <option value="">Select...</option>
              <option value="25/50">25/50</option>
              <option value="50/100">50/100</option>
              <option value="100/300">100/300</option>
              <option value="250/500">250/500</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.uninsuredMotorist}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Label>Extras</Form.Label>
          <div className="d-flex flex-column">
            <Form.Check
              type="switch"
              id="roadsideAssistance"
              label="Include roadside assistance"
              checked={coverage.roadsideAssistance}
              onChange={(e) =>
                setCoverage((prev) => ({
                  ...prev,
                  roadsideAssistance: e.target.checked,
                }))
              }
            />
            <Form.Check
              type="switch"
              id="rentalReimbursement"
              label="Include rental reimbursement"
              checked={coverage.rentalReimbursement}
              onChange={(e) =>
                setCoverage((prev) => ({
                  ...prev,
                  rentalReimbursement: e.target.checked,
                }))
              }
            />
          </div>
        </Col>
      </Row>
    </>
  );

  const renderReviewStep = () => (
    <>
      <Row className="mb-3">
        <Col>
          <Alert variant="info">
            Please review the information below. We’ll use this to shop multiple
            carriers and find your best rate and coverage options.
          </Alert>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Driver</Card.Header>
            <Card.Body>
              <div><strong>Name:</strong> {driver.firstName} {driver.lastName}</div>
              <div><strong>Email:</strong> {driver.email}</div>
              <div><strong>Phone:</strong> {driver.phone}</div>
              <div><strong>DOB:</strong> {driver.dateOfBirth}</div>
              <div><strong>License:</strong> {driver.licenseState} {driver.licenseNumber}</div>
              <div><strong>Marital Status:</strong> {driver.maritalStatus || "-"}</div>
              <div><strong>SR-22 Needed:</strong> {driver.sr22Needed || "-"}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Vehicle</Card.Header>
            <Card.Body>
              <div>
                <strong>Vehicle:</strong>{" "}
                {vehicle.year} {vehicle.make} {vehicle.model}
              </div>
              <div><strong>VIN:</strong> {vehicle.vin || "-"}</div>
              <div><strong>Garaging ZIP:</strong> {vehicle.garagingZip}</div>
              <div><strong>Primary Use:</strong> {vehicle.primaryUse || "-"}</div>
              <div><strong>Annual Mileage:</strong> {vehicle.annualMileage || "-"}</div>
              <div><strong>Ownership:</strong> {vehicle.ownership || "-"}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Card>
            <Card.Header>Coverage Preferences</Card.Header>
            <Card.Body>
              <div><strong>Coverage Level:</strong> {coverage.coverageLevel || "-"}</div>
              <div><strong>Collision Deductible:</strong> {coverage.collisionDeductible || "-"}</div>
              <div><strong>Comprehensive Deductible:</strong> {coverage.comprehensiveDeductible || "-"}</div>
              <div><strong>UM/UIM Limits:</strong> {coverage.uninsuredMotorist || "-"}</div>
              <div>
                <strong>Roadside Assistance:</strong>{" "}
                {coverage.roadsideAssistance ? "Yes" : "No"}
              </div>
              <div>
                <strong>Rental Reimbursement:</strong>{" "}
                {coverage.rentalReimbursement ? "Yes" : "No"}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>Contact & Consent</Card.Header>
            <Card.Body>
              <Form.Group controlId="preferredContactMethod" className="mb-2">
                <Form.Label>Preferred Contact Method</Form.Label>
                <Form.Select
                  value={meta.preferredContactMethod}
                  isInvalid={hasError("preferredContactMethod")}
                  onChange={(e) =>
                    setMeta((prev) => ({
                      ...prev,
                      preferredContactMethod: e.target.value,
                    }))
                  }
                >
                  <option value="">Select...</option>
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="text">Text Message (where allowed)</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.preferredContactMethod}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="bestTimeToContact" className="mb-2">
                <Form.Label>Best Time to Contact</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Weekdays after 5pm"
                  value={meta.bestTimeToContact}
                  isInvalid={hasError("bestTimeToContact")}
                  onChange={(e) =>
                    setMeta((prev) => ({
                      ...prev,
                      bestTimeToContact: e.target.value,
                    }))
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bestTimeToContact}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="consentToContact">
                <Form.Check
                  type="checkbox"
                  label="I agree to be contacted about my quote by phone, email, or text."
                  checked={meta.consentToContact}
                  isInvalid={hasError("consentToContact")}
                  onChange={(e) =>
                    setMeta((prev) => ({
                      ...prev,
                      consentToContact: e.target.checked,
                    }))
                  }
                />
                {hasError("consentToContact") && (
                  <div className="text-danger small mt-1">
                    {errors.consentToContact}
                  </div>
                )}
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {submitted && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success">
              <strong>Thank you!</strong> Your quote request has been received.
              An agent will review your information and reach out with options.
            </Alert>
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8} xl={7}>
          <Card className="shadow-sm">
            <Card.Body>
              {renderStepHeader()}
              <ProgressBar
                now={progress}
                className="mb-4"
                animated
                visuallyHidden
              />

              <Form onSubmit={handleSubmit}>
                {step === 1 && renderDriverStep()}
                {step === 2 && renderVehicleStep()}
                {step === 3 && renderCoverageStep()}
                {step === 4 && renderReviewStep()}

                {Object.keys(errors).length > 0 && (
                  <Alert variant="danger" className="mt-3">
                    Please fix the highlighted fields before continuing.
                  </Alert>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBack}
                    disabled={step === 1 || submitting}
                  >
                    Back
                  </Button>

                  {step < totalSteps && (
                    <Button variant="primary" onClick={handleNext} disabled={submitting}>
                      Next
                    </Button>
                  )}

                  {step === totalSteps && (
                    <Button variant="success" type="submit" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Submitting…
                        </>
                      ) : (
                        "Request My Quote"
                      )}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Optional Right Sidebar */}
        <Col lg={4} className="mt-4 mt-lg-0">
          <Card className="h-100">
            <Card.Body>
              <h5>What Happens Next?</h5>
              <ul className="small mb-3">
                <li>We securely receive your info.</li>
                <li>We shop multiple top-rated carriers.</li>
                <li>You get options for price & coverage.</li>
              </ul>
              <h6>Need help now?</h6>
              <p className="mb-1">
                <TelephoneFill className="me-2" />
                Call: (555) 123-4567
              </p>
              <p className="mb-0">
                <EnvelopeFill className="me-2" />
                Email: quotes@exampleagency.com
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AutoInsuranceQuoteFunnel_2025_11_27_chat;
